import type { GISFormat, FormatDetectionResult } from '../types/gisFormat';
import { UnsupportedFormatError } from '../types/gisFormat';
import { unzipSync } from 'fflate';

/**
 * Map of file extensions to GIS formats.
 * Includes primary and secondary extensions for each format.
 */
const EXTENSION_MAP: Record<string, GISFormat> = {
  '.kml': 'kml',
  '.kmz': 'kml',
  '.shp': 'shapefile',
  '.shx': 'shapefile',
  '.dbf': 'shapefile',
  '.prj': 'shapefile',
  '.cpg': 'shapefile',
  '.gpkg': 'geopackage',
  '.geojson': 'geojson',
  '.json': 'geojson',
  '.gpx': 'gpx',
  '.zip': 'shapefile', // ZIP files default to shapefiles unless containing .kml
};

/**
 * Magic bytes signatures for binary format detection.
 */
const MAGIC_BYTES: { bytes: number[]; offset: number; format: GISFormat }[] = [
  // SQLite header (GeoPackage is a SQLite database)
  {
    bytes: [0x53, 0x51, 0x4c, 0x69, 0x74, 0x65, 0x20, 0x66, 0x6f, 0x72, 0x6d, 0x61, 0x74],
    offset: 0,
    format: 'geopackage',
  },
  // Shapefile .shp magic number (file code 9994 as big-endian int32)
  { bytes: [0x00, 0x00, 0x27, 0x0a], offset: 0, format: 'shapefile' },
];

/**
 * Detects the GIS format of a file based on its extension.
 *
 * @param filename - The name of the file (with extension)
 * @returns The detected GISFormat or null if extension is not recognized
 */
export function detectFormatFromExtension(filename: string): GISFormat | null {
  if (!filename) return null;

  const lastDotIndex = filename.lastIndexOf('.');
  if (lastDotIndex === -1) return null;

  const extension = filename.slice(lastDotIndex).toLowerCase();
  return EXTENSION_MAP[extension] ?? null;
}

/**
 * Detects the GIS format from file content by inspecting magic bytes
 * and text content patterns.
 *
 * @param content - ArrayBuffer of the file content (at least first 1024 bytes)
 * @returns The detected GISFormat or null if content is not recognized
 */
export function detectFormatFromContent(content: ArrayBuffer): GISFormat | null {
  if (!content || content.byteLength === 0) return null;

  const bytes = new Uint8Array(content);

  // Check magic bytes for binary formats (GeoPackage SQLite, raw Shapefile .shp)
  for (const signature of MAGIC_BYTES) {
    if (matchesMagicBytes(bytes, signature.bytes, signature.offset)) {
      return signature.format;
    }
  }

  // Check for ZIP archive (PK\x03\x04 header)
  if (matchesMagicBytes(bytes, [0x50, 0x4b, 0x03, 0x04], 0)) {
    try {
      const unzipped = unzipSync(bytes);
      const fileNames = Object.keys(unzipped);

      if (fileNames.some((name) => name.toLowerCase().endsWith('.kml'))) {
        return 'kml';
      }
      if (fileNames.some((name) => name.toLowerCase().endsWith('.shp'))) {
        return 'shapefile';
      }
    } catch {
      // Unzipping partial slice failed
    }

    return 'shapefile';
  }

  // For text-based formats, decode and inspect content
  const textContent = decodeTextContent(bytes);
  if (textContent === null) return null;

  return detectTextFormat(textContent);
}

/**
 * Checks if the byte array matches a magic byte signature at the given offset.
 */
function matchesMagicBytes(data: Uint8Array, signature: number[], offset: number): boolean {
  if (data.length < offset + signature.length) return false;

  for (let i = 0; i < signature.length; i++) {
    if (data[offset + i] !== signature[i]) return false;
  }
  return true;
}

/**
 * Attempts to decode the first portion of a byte array as UTF-8 text.
 * Returns null if the content appears to be binary.
 */
function decodeTextContent(bytes: Uint8Array): string | null {
  // Check if content looks like text (no null bytes in first 512 bytes)
  const checkLength = Math.min(bytes.length, 512);
  for (let i = 0; i < checkLength; i++) {
    if (bytes[i] === 0) return null;
  }

  try {
    const decoder = new TextDecoder('utf-8');
    const sampleLength = Math.min(bytes.length, 4096);
    return decoder.decode(bytes.slice(0, sampleLength));
  } catch {
    return null;
  }
}

/**
 * Detects the format of text-based GIS files by inspecting content patterns.
 */
function detectTextFormat(text: string): GISFormat | null {
  const trimmed = text.trimStart();

  // KML detection: XML with <kml namespace
  if (isKML(trimmed)) return 'kml';

  // GPX detection: XML with <gpx root element
  if (isGPX(trimmed)) return 'gpx';

  // GeoJSON detection: JSON with "type" field matching GeoJSON types
  if (isGeoJSON(trimmed)) return 'geojson';

  return null;
}

/**
 * Checks if text content is KML format.
 * KML files are XML documents with a <kml> root element or kml namespace.
 */
function isKML(text: string): boolean {
  // Check for XML declaration or direct <kml tag
  if (text.startsWith('<?xml') || text.startsWith('<kml') || text.startsWith('<KML')) {
    return /<kml[\s>]/i.test(text);
  }
  return false;
}

/**
 * Checks if text content is GPX format.
 * GPX files are XML documents with a <gpx> root element.
 */
function isGPX(text: string): boolean {
  if (text.startsWith('<?xml') || text.startsWith('<gpx') || text.startsWith('<GPX')) {
    return /<gpx[\s>]/i.test(text);
  }
  return false;
}

/**
 * Checks if text content is GeoJSON format.
 * GeoJSON files are JSON with a "type" field matching GeoJSON types.
 */
function isGeoJSON(text: string): boolean {
  // Quick check: must start with { or [
  if (!text.startsWith('{') && !text.startsWith('[')) return false;

  try {
    const parsed = JSON.parse(text);
    if (typeof parsed !== 'object' || parsed === null) return false;

    const geoJSONTypes = [
      'FeatureCollection',
      'Feature',
      'Point',
      'MultiPoint',
      'LineString',
      'MultiLineString',
      'Polygon',
      'MultiPolygon',
      'GeometryCollection',
    ];

    return geoJSONTypes.includes(parsed.type);
  } catch {
    // If JSON parsing fails on the sample, check for GeoJSON patterns
    // This handles cases where the sample is truncated
    return /"type"\s*:\s*"(FeatureCollection|Feature|Point|MultiPoint|LineString|MultiLineString|Polygon|MultiPolygon|GeometryCollection)"/.test(
      text
    );
  }
}

/**
 * Detects the GIS format of a file using both extension and content analysis.
 *
 * Strategy:
 * 1. Try extension-based detection first (fast)
 * 2. If content is provided, also try content-based detection (more reliable)
 * 3. If both agree, return high confidence
 * 4. If only one succeeds, return medium confidence
 * 5. If they disagree, prefer content-based detection
 *
 * @param file - The File object to detect format for
 * @returns Promise resolving to FormatDetectionResult
 * @throws UnsupportedFormatError if format cannot be determined
 */
export async function detectFormat(file: File): Promise<FormatDetectionResult> {
  const extensionFormat = detectFormatFromExtension(file.name);

  // Read the first 4KB for content inspection
  const contentSlice = file.slice(0, 4096);
  const content = await contentSlice.arrayBuffer();
  const contentFormat = detectFormatFromContent(content);

  // Both methods agree
  if (extensionFormat && contentFormat && extensionFormat === contentFormat) {
    return {
      format: extensionFormat,
      confidence: 'high',
      detectedBy: 'both',
    };
  }

  // Handle KMZ files (.kmz extension + ZIP binary content)
  if (extensionFormat === 'kml' && file.name.toLowerCase().endsWith('.kmz')) {
    return {
      format: 'kml',
      confidence: 'high',
      detectedBy: 'both',
    };
  }

  // Content detection succeeded (more reliable than extension)
  if (contentFormat) {
    return {
      format: contentFormat,
      confidence: extensionFormat ? 'medium' : 'medium',
      detectedBy: extensionFormat ? 'content' : 'content',
    };
  }

  // Only extension detection succeeded
  if (extensionFormat) {
    return {
      format: extensionFormat,
      confidence: 'medium',
      detectedBy: 'extension',
    };
  }

  // Neither method could detect the format
  throw new UnsupportedFormatError(
    `Unable to detect GIS format for file "${file.name}". Supported formats: KML, Shapefile (.shp/.zip), GeoPackage (.gpkg), GeoJSON, GPX.`,
    file.name
  );
}
